import React from "react";
import axios from "axios";
import { useModalContext } from "components/modal";
import { useAreYouSure } from "components/areYouSure";
import { useSnackbarContext } from "components/snackbar";
import "react-confirm-alert/src/react-confirm-alert.css";
import { IAppointmentType } from "../types/IAppointmentType";
import { EditAppointmentType } from "./EditAppointmentType";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { AppointmentRow } from "./AppointmentRow";

type Props = {
  appointmentType: IAppointmentType;
  archived?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
};

export const AppointmentType = ({
  appointmentType,
  archived,
  isExpanded = false,
  onToggleExpand,
}: Props) => {
  const { id, label, color, defaults = [], item_target_id } = appointmentType;

  const queryClient = useQueryClient();
  const areYouSure = useAreYouSure();
  const { showSnackbar } = useSnackbarContext();
  const { setModal } = useModalContext();

  const { t } = useTranslation();
  const tBase = "views.admin.appointment-types.components.AppointmentType";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const activeDefaults = defaults.filter((d) => d.active);
  const hasDefaults = defaults.length > 0;

  const handleClickEdit = (type: "parent" | "child", mode: "edit" | "new") => {
    setModal({
      label: mode === "edit" ? tr("Edit Appointment Type") : tr("Add Appointment Type"),
      component: (
        <EditAppointmentType type={type} mode={mode} appointmentType={appointmentType} />
      ),
    });
  };

  const handleClickAddDefault = () => {
    setModal({
      label: tr("Add Default"),
      component: (
        <EditAppointmentType
          type="child"
          mode="new"
          appointmentType={appointmentType}
          appointmentTypeId={appointmentType.id}
          parentColor={appointmentType.color}
        />
      ),
    });
  };

  const handleClickArchive = async (categoryId: number) => {
    areYouSure.activate(
      () => {
        axios
          .post(
            `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-types/archive/${categoryId}`
          )
          .then(() => {
            queryClient.invalidateQueries();
            showSnackbar(tr("Category archived successfully."));
          })
          .catch(() => {
            showSnackbar(tr("Failed to archive category."));
          });
      },
      { message: tr("Are you sure you want to perform this action?") }
    );
  };

  const handleClickRestore = async (categoryId: number) => {
    areYouSure.activate(
      () => {
        axios
          .post(
            `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-types/restore/${categoryId}`
          )
          .then(() => {
            queryClient.invalidateQueries();
            showSnackbar(tr("Category restored successfully."));
          })
          .catch(() => {
            showSnackbar(tr("Failed to restore category."));
          });
      },
      { message: tr("Are you sure you want to perform this action?") }
    );
  };

  return (
    <div className={isExpanded ? "mt-2 mb-0" : "mt-2 mb-2"}>
      <AppointmentRow
        id={id}
        label={label}
        color={color}
        archived={archived}
        isExpanded={isExpanded}
        showExpand={hasDefaults}
        defaultsCount={hasDefaults ? activeDefaults.length : undefined}
        defaultsLabel={"Sub-Types"}
        archiveLabel={tr("Archive")}
        restoreLabel="Restore"
        editLabel={tr("Edit")}
        onToggleExpand={onToggleExpand}
        onArchive={() => handleClickArchive(id)}
        onRestore={() => handleClickRestore(id)}
        onEdit={() => handleClickEdit("parent", "edit")}
        addDefaultLabel={"Add Sub-Type"}
        onAddDefault={handleClickAddDefault}
      />
    </div>
  );
};
